/**
 * Rules — a team's engineering standards as RFC 2119 statements, the product
 * form of the Codex this repository is governed by (`codex/`). They live in a
 * rules repository of their own (`repository.ts`), one JSON file per RFC
 * (`0012-api-errors.json`): the workspace's. Its folder names the code
 * repositories it reaches (`api/`, `api+web/`), at the top level every one.
 *
 *   {
 *     "rfc": 12, "slug": "api-errors", "title": "API errors",
 *     "status": "enforced",                    draft | approved | enforced | superseded
 *     "summary": "How the API reports a failure.",
 *     "scope": { "paths": ["src/**"] },        optional: absent is all of its repositories
 *     "statements": [
 *       { "slug": "problem-json", "level": "MUST",
 *         "text": "An error response MUST be application/problem+json.",
 *         "why": "…", "paths": ["src/http/**"], "status": "approved",
 *         "check": "checks/problem-json.py" }      optional: a script decides it, not an agent
 *     ]
 *   }
 *
 * A file this repository's own Codex wrote reads too: its extra fields
 * (`domain`, `sdlc`, `sources`, `related`, `updated`, `scope.projects`, a
 * statement's `section`, `enforcement`, `href`, `spikes`, its `rationale` — read
 * as the statement's `why` — and a `check` under the
 * Codex's own `codex/checks/`) are kept out of the way rather than refused, so a
 * team can start from the Codex's shape.
 *
 * A statement's **check** names a Python script of the same rules repository
 * (`checks/<name>.py`, `CHECK_FILE`): an audit runs it in the session's machine
 * on the files of the change the statement applies to, and its answer is the
 * statement's finding — no agent reads for it. The script's contract is the
 * Codex's own (`codex/lib/checks.mjs`): `python3 -I <script> <file>…` from the
 * repository's root, exit 0 when the files honour the statement, 1 printing
 * one `path:line: what is wrong` per line when they do not, anything else a
 * check that did not run.
 *
 * What a statement's status means is the Codex's (0001/status-lifecycle): a
 * draft is not applied, approved is applied, **enforced** is applied and an
 * enforced MUST or MUST NOT found violated holds a merge; a superseded RFC is
 * history. A statement's own status overrides its RFC's.
 *
 * A glob of an RFC in a folder of several repositories may name one of them in
 * front (`web:src/client/**`, `repository.ts`); without one it reaches each
 * repository its folder names — every repository of the session at the top
 * level, where a glob names none.
 *
 * Everything here is pure: the desktop reads the files out of Git and hands
 * their text in, the account hands in what its GitHub App read or what an
 * administrator typed. Both refuse with the same sentences
 * (1020/one-validator-both-sides).
 */
import { isPlainObject } from '../workspace/shape';
import { CHECK_FILE, MAX_RULE_REPOS, REPO_NAME, RULE_FILE, ruleFileName, ruleFileRepos, ruleFolder, splitRepoGlob } from './repository';

export const RULE_LEVELS = ['MUST', 'MUST NOT', 'SHOULD', 'SHOULD NOT', 'MAY'] as const;
export type RuleLevel = (typeof RULE_LEVELS)[number];

export const RULE_STATUSES = ['draft', 'approved', 'enforced', 'superseded'] as const;
export type RuleStatus = (typeof RULE_STATUSES)[number];
/** A statement's own status; superseded is an RFC's alone. */
export const STATEMENT_STATUSES = ['draft', 'approved', 'enforced'] as const;

/** Bounds, so a repository cannot make a session read without end. */
export const MAX_RULE_FILES = 200;
export const MAX_RULE_FILE_CHARS = 256 * 1024;
export const MAX_STATEMENTS = 2000;
export const MAX_STATEMENT_CHARS = 4000;
export const MAX_RULE_GLOBS = 50;

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const GLOB = /^[^\0\r\n]{1,300}$/;
const RFC_KEYS = new Set(['rfc', 'slug', 'title', 'status', 'supersededBy', 'summary', 'scope', 'statements']);
/** The Codex's own fields, read past without a word. */
const CODEX_RFC_KEYS = new Set(['domain', 'sdlc', 'sources', 'related', 'updated']);
const STATEMENT_KEYS = new Set(['slug', 'level', 'status', 'text', 'why', 'paths', 'section', 'check']);
const CODEX_STATEMENT_KEYS = new Set(['enforcement', 'href', 'spikes', 'rationale']);
/** The Codex's own checks (`codex/checks/<name>.py|mjs`), run by the Codex and read past here. */
const CODEX_CHECK = /^codex\/checks\//;

export interface RuleStatementDoc {
  slug: string;
  level: RuleLevel;
  text: string;
  why?: string;
  paths?: string[];
  status?: (typeof STATEMENT_STATUSES)[number];
  section?: string[];
  /** Its check script in the rules repository, `checks/<name>.py`. */
  check?: string;
}

export interface RuleRfc {
  rfc: number;
  slug: string;
  title: string;
  status: RuleStatus;
  supersededBy?: number;
  summary: string;
  scope?: { paths?: string[] };
  statements: RuleStatementDoc[];
  /**
   * The projects its file's folder names (`ruleFileRepos`), lower
   * case and sorted; absent at the top level — every repository. Never a field
   * of the file: the folder says it, and an edit carries it to say where the
   * file goes (`ruleFile`).
   */
  repos?: string[];
}

/**
 * One statement as the queries read it: its RFC's identity carried with it.
 * `id` is `<origin>:<rfc>/<slug>` — `ws:4/slug` for the workspace's rules
 * repository, `airlock:1/slug` for Airlock's own statements about one — so two
 * sources never collide.
 */
export interface Rule {
  id: string;
  origin: string;
  rfc: number;
  rfcTitle: string;
  slug: string;
  level: RuleLevel;
  status: RuleStatus;
  text: string;
  why: string | null;
  section: string[];
  /** Its own globs, else its RFC's scope; empty is all of `repos`. A glob may name one of them in front (`web:src/**`). */
  paths: string[];
  /** The repositories its RFC's folder names; empty is every repository. */
  repos: string[];
  ownPaths: boolean;
  /** The script that decides it (`checks/<name>.py` of its rules repository), or null when an agent reads for it. */
  check: string | null;
}

export interface RuleSet {
  rfcs: RuleRfc[];
  rules: Rule[];
  /** One sentence per file or statement refused, `<file>: <why>`. A refused file is not loaded; the rest are. */
  problems: string[];
}

export const EMPTY_RULE_SET: RuleSet = { rfcs: [], rules: [], problems: [] };

const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((one) => typeof one === 'string');

function globs(value: unknown, where: string, problems: string[], repos: readonly string[]): string[] | null {
  if (value === undefined) return [];
  if (!strings(value) || value.length > MAX_RULE_GLOBS || !value.every((one) => GLOB.test(one))) {
    problems.push(`${where}: a list of at most ${MAX_RULE_GLOBS} path globs (\`src/**\`, \`web:src/*.ts\` in a folder of several projects).`);
    return null;
  }
  const before = problems.length;
  for (const glob of value) {
    // The remote in front a glob carried before folders named its repositories.
    if (/^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)+:/.test(glob)) {
      problems.push(`${where}: ${glob} — a project is named by the RFC's folder (\`web/\`, \`api+web/\`), not by its remote.`);
      continue;
    }
    const named = splitRepoGlob(glob).repo;
    if (!named) continue;
    if (!repos.length) problems.push(`${where}: ${glob} — an RFC at the top level reaches every repository; put it in the folder of the projects it names.`);
    else if (!repos.includes(named)) problems.push(`${where}: ${glob} — ${named} is not a project of its folder (\`${ruleFolder(repos)}/\`).`);
  }
  return problems.length > before ? null : value.map((one) => one.replace(/^\.?\//, ''));
}

/** A `repos` an edit carries, or null with a sentence. */
function reposOf(value: unknown, at: (msg: string) => void): string[] | null {
  if (value === undefined) return [];
  if (!strings(value) || value.length > MAX_RULE_REPOS || !value.every((one) => REPO_NAME.test(one))) {
    at(`repos is a list of at most ${MAX_RULE_REPOS} project folder names (\`web\`), as Settings › Rules names them.`);
    return null;
  }
  return [...new Set(value.map((one) => one.toLowerCase()))].sort();
}

/**
 * One RFC as a file or an edit wrote it, or null with the reasons. `where`
 * names it in each sentence — the file, or `rfc 12`. A file is read with the
 * projects its folder names (`folder`, `ruleFileRepos`) and carries no
 * `repos` of its own; an edit, read with none, says them in `repos`.
 */
export function parseRuleRfc(input: unknown, where: string, problems: string[], folder?: readonly string[]): RuleRfc | null {
  const before = problems.length;
  const at = (msg: string): void => {
    problems.push(`${where}: ${msg}`);
  };
  if (!isPlainObject(input)) {
    at('Expected an object: { rfc, slug, title, status, summary, statements }.');
    return null;
  }
  for (const key of Object.keys(input))
    if (!RFC_KEYS.has(key) && !CODEX_RFC_KEYS.has(key) && (key !== 'repos' || folder))
      at(key === 'repos' ? 'its folder names its projects (`web/`, `api+web/`), not a "repos" field.' : `"${key}" is not a field of an RFC.`);
  const { rfc, slug, title, status, summary, supersededBy } = input;
  const repos = folder ? [...folder] : (reposOf(input.repos, at) ?? []);
  if (repos.length > MAX_RULE_REPOS) at(`its folder names ${repos.length} repositories — at most ${MAX_RULE_REPOS}.`);
  if (typeof rfc !== 'number' || !Number.isInteger(rfc) || rfc < 1 || rfc > 99_999) at('rfc is its number, a whole number from 1.');
  if (typeof slug !== 'string' || !SLUG.test(slug)) at('slug is kebab-case (`api-errors`).');
  if (typeof title !== 'string' || title.trim().length < 3 || title.length > 200) at('title is 3 to 200 characters.');
  if (typeof summary !== 'string' || summary.trim().length < 10 || summary.length > 2000) at('summary is 10 to 2000 characters.');
  if (typeof status !== 'string' || !(RULE_STATUSES as readonly string[]).includes(status)) at(`status is one of ${RULE_STATUSES.join(', ')}.`);
  if (status === 'superseded' && (typeof supersededBy !== 'number' || !Number.isInteger(supersededBy))) at('a superseded RFC names supersededBy.');
  let scope: RuleRfc['scope'];
  if (input.scope !== undefined) {
    if (!isPlainObject(input.scope)) at('scope is { "paths": [globs] }.');
    else {
      const paths = globs(input.scope.paths, `${where}: scope.paths`, problems, repos);
      if (paths?.length) scope = { paths };
    }
  }
  const statements: RuleStatementDoc[] = [];
  if (!Array.isArray(input.statements) || !input.statements.length) at('statements is a non-empty list.');
  else {
    const seen = new Set<string>();
    input.statements.forEach((raw, index) => {
      const one = parseStatement(raw, `${where}: statement ${isPlainObject(raw) && typeof raw.slug === 'string' ? raw.slug : `#${index + 1}`}`, problems, status, repos);
      if (!one) return;
      if (seen.has(one.slug)) problems.push(`${where}: statement ${one.slug} is listed twice.`);
      seen.add(one.slug);
      statements.push(one);
    });
  }
  if (problems.length > before) return null;
  return {
    rfc: rfc as number,
    slug: slug as string,
    title: (title as string).trim(),
    status: status as RuleStatus,
    ...(typeof supersededBy === 'number' ? { supersededBy } : {}),
    summary: (summary as string).trim(),
    ...(scope ? { scope } : {}),
    statements,
    ...(repos.length ? { repos } : {})
  };
}

/** Where an RFC is written and what — its file's text carries no `repos`, its folder does. */
export function ruleFile(rfc: RuleRfc): { path: string; text: string } {
  const { repos, ...doc } = rfc;
  return { path: ruleFileName(rfc.rfc, rfc.slug, repos), text: `${JSON.stringify(doc, null, 2)}\n` };
}

function parseStatement(
  input: unknown,
  where: string,
  problems: string[],
  rfcStatus: unknown,
  repos: readonly string[]
): RuleStatementDoc | null {
  const before = problems.length;
  const at = (msg: string): void => {
    problems.push(`${where}: ${msg}`);
  };
  if (!isPlainObject(input)) {
    at('Expected an object: { slug, level, text }.');
    return null;
  }
  for (const key of Object.keys(input)) if (!STATEMENT_KEYS.has(key) && !CODEX_STATEMENT_KEYS.has(key)) at(`"${key}" is not a field of a statement.`);
  const { slug, level, text, status } = input;
  // The Codex calls it `rationale`: read as the statement's why when it says none.
  const why = input.why ?? input.rationale;
  if (typeof slug !== 'string' || !SLUG.test(slug)) at('slug is kebab-case.');
  if (typeof level !== 'string' || !(RULE_LEVELS as readonly string[]).includes(level)) at(`level is one of ${RULE_LEVELS.join(', ')}.`);
  else if (typeof text !== 'string' || !text.includes(level)) at(`text says its level in capitals ("${level}").`);
  if (typeof text !== 'string' || text.trim().length < 10 || text.length > MAX_STATEMENT_CHARS)
    at(`text is 10 to ${MAX_STATEMENT_CHARS} characters.`);
  if (why !== undefined && (typeof why !== 'string' || why.length > MAX_STATEMENT_CHARS)) at('why is text.');
  if (status !== undefined) {
    if (typeof status !== 'string' || !(STATEMENT_STATUSES as readonly string[]).includes(status)) at(`status is one of ${STATEMENT_STATUSES.join(', ')}.`);
    else if (rfcStatus === 'superseded') at("a superseded RFC's statements carry no status of their own.");
  }
  if (input.section !== undefined && !strings(input.section)) at('section is a list of headings.');
  const check = typeof input.check === 'string' && CHECK_FILE.test(input.check) ? input.check : undefined;
  if (input.check !== undefined && !check && !(typeof input.check === 'string' && CODEX_CHECK.test(input.check)))
    at('check is a Python script of the rules repository, `checks/<name>.py` in kebab-case.');
  const paths = globs(input.paths, `${where}: paths`, problems, repos);
  if (problems.length > before) return null;
  return {
    slug: slug as string,
    level: level as RuleLevel,
    text: (text as string).trim(),
    ...(typeof why === 'string' && why.trim() ? { why: why.trim() } : {}),
    ...(paths?.length ? { paths } : {}),
    ...(typeof status === 'string' ? { status: status as RuleStatementDoc['status'] } : {}),
    ...(strings(input.section) && input.section.length ? { section: input.section } : {}),
    ...(check ? { check } : {})
  };
}

/** The statements of these RFCs, flattened, each with its RFC's identity. */
export function rulesOf(rfcs: readonly RuleRfc[], origin = ''): Rule[] {
  const prefix = origin ? `${origin}:` : '';
  return [...rfcs]
    .sort((a, b) => a.rfc - b.rfc)
    .flatMap((rfc) =>
      rfc.statements.map((s) => ({
        id: `${prefix}${rfc.rfc}/${s.slug}`,
        origin,
        rfc: rfc.rfc,
        rfcTitle: rfc.title,
        slug: s.slug,
        level: s.level,
        status: rfc.status === 'superseded' ? 'superseded' : (s.status ?? rfc.status),
        text: s.text,
        why: s.why ?? null,
        section: s.section ?? [],
        paths: s.paths ?? rfc.scope?.paths ?? [],
        repos: rfc.repos ?? [],
        ownPaths: Boolean(s.paths?.length),
        check: s.check ?? null
      }))
    );
}

/**
 * A rules repository's RFCs from its files' text, `{ name, text }` named by
 * their path in it; `label` names the repository in each sentence. A file
 * whose path is not an RFC's (`repository.ts`, `RULE_FILE`) is not read — a
 * README is no rule — and one that does not parse, or is refused, is named in
 * `problems` and left out; an RFC number two files claim keeps the first,
 * whichever folders they are in.
 */
export function readRuleFiles(files: readonly { name: string; text: string }[], label = 'rules'): RuleSet {
  const problems: string[] = [];
  const rfcs: RuleRfc[] = [];
  const numbers = new Map<number, string>();
  const kept = [...files].filter((one) => RULE_FILE.test(one.name)).sort((a, b) => a.name.localeCompare(b.name));
  if (kept.length > MAX_RULE_FILES) problems.push(`${label}: ${kept.length} files — only the first ${MAX_RULE_FILES} are read.`);
  for (const file of kept.slice(0, MAX_RULE_FILES)) {
    const where = file.name;
    if (file.text.length > MAX_RULE_FILE_CHARS) {
      problems.push(`${where}: over ${MAX_RULE_FILE_CHARS / 1024} KB.`);
      continue;
    }
    let raw: unknown;
    try {
      raw = JSON.parse(file.text);
    } catch {
      problems.push(`${where}: not JSON.`);
      continue;
    }
    const rfc = parseRuleRfc(raw, where, problems, ruleFileRepos(file.name));
    if (!rfc) continue;
    const other = numbers.get(rfc.rfc);
    if (other) {
      problems.push(`${where}: rfc ${rfc.rfc} is also ${other}.`);
      continue;
    }
    numbers.set(rfc.rfc, where);
    rfcs.push(rfc);
  }
  let rules = rulesOf(rfcs);
  if (rules.length > MAX_STATEMENTS) {
    problems.push(`${label}: ${rules.length} statements — only the first ${MAX_STATEMENTS} are read.`);
    rules = rules.slice(0, MAX_STATEMENTS);
  }
  return { rfcs, rules, problems };
}
